import { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import type { CSSProperties, MouseEvent, Ref } from 'react';
import ChooseIcon from '@src/assets/choose.svg?react';
import classNames from 'classnames';
import styles from './FJCheckBox.module.less';

interface FJCheckBoxRef {
    selected: () => boolean;
    value: () => string;
    select: () => void;
    deselect: () => void;
}

interface FJCheckBoxProps {
    selected?: boolean;
    value?: string;
    style?: CSSProperties;
    dataChange?: (selected: boolean, e: MouseEvent<HTMLDivElement>) => void;
    className?: string;
    iconClassName?: string;
    type?: keyof typeof styles | string;
    textClassName?: string;
    disableReselect?: boolean;
    disable?: boolean;
}

function FJCheckBox(props: FJCheckBoxProps, ref: Ref<FJCheckBoxRef>) {
    const {
        selected: defaultSelected = false,
        value = '',
        style,
        dataChange,
        className,
        iconClassName,
        type,
        textClassName,
        disable = false,
        disableReselect = false,
    } = props;
    const [isSelected, setSelected] = useState(defaultSelected);

    useEffect(() => {
        setSelected(defaultSelected);
    }, [defaultSelected]);

    useImperativeHandle(ref, () => ({
        selected: () => {
            return isSelected;
        },
        value: () => {
            return value;
        },
        select: () => {
            setSelected(true);
        },
        deselect: () => {
            setSelected(false);
        },
    }));

    const _handleClick = (e: MouseEvent<HTMLDivElement>) => {
        if (disable) {
            return;
        }
        const nextSelected = !isSelected;
        if (disableReselect && !nextSelected) {
            return;
        }
        setSelected(nextSelected);
        if (dataChange) {
            dataChange(nextSelected, e);
        }
    };

    return (
        <div
            onClick={_handleClick}
            className={classNames(
                styles.check_box,
                className,
                isSelected ? styles.selected : '',
                disable ? styles.disabled : '',
            )}
            style={style}
        >
            <div
                className={classNames(
                    styles.check_box_select,
                    iconClassName,
                    type && styles[type as keyof typeof styles],
                )}
            >
                <div className={styles.inner}>
                    <ChooseIcon className={styles.icon} />
                </div>
            </div>

            {value && <span className={classNames(styles.check_box_value, textClassName)}>{value}</span>}
        </div>
    );
}

export default forwardRef<FJCheckBoxRef, FJCheckBoxProps>(FJCheckBox);
